import { FC, useEffect, useRef, useState } from 'react';
import { FastField, FormikProps, useFormikContext } from 'formik';
import { Button, FormControl, FormControlLabel, Grid, Radio } from '@mui/material';
import { DateRangeField } from '../../formFields/DateRangeField';
import { IUploadEvent } from '../modals/UploadEvent/queries';
import { familyRolesCalendar, paymentPlatformType, platformType } from '../../../shared/queries/family';

import {
  ApplyWrapper,
  RadioGroupWrapper,
  ReportsBlockWrapper,
  RightSearchParams,
  RightSearchParamsBtn,
  UploadEntityFormDate,
  UploadEntityFormRow,
  UploadFormLabel,
} from '../documents/styles';
import { IDateStatistics } from './interfaces';
import axios from 'axios';
import { TextInputField } from '../../formFields/TextInputField';
import { SelectField } from '../../formFields/SelectField';

interface IAddEventModalFormProps {
  formikRef: React.RefObject<FormikProps<IUploadEvent> | null>;
  values: any;
  setFieldValue: any;
  familyCreator: any;
  setData: any;
  getUserReports: any;
  getStatisticsCreatorsInfo: any;
  getStatisticsUsersInfo: any;
  getSuperAdminReports: any;
  setPage: (page: number) => void;
  pageSize: number;
  getUnSubscripeGoogleEvent: () => void;
}

export const DatePickerStatistics: FC<IAddEventModalFormProps> = ({
  formikRef,
  values,
  setFieldValue,
  setData,
  familyCreator,
  getStatisticsCreatorsInfo,
  getStatisticsUsersInfo,
  getUserReports,
  getSuperAdminReports,
  setPage,
  pageSize,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { values: formikValues } = useFormikContext<IDateStatistics>();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    if (debouncedSearch.trim() === '') return;

    axios
      .get(`/api/v1/Statistics/users-search?query=${encodeURIComponent(debouncedSearch)}`)
      .then((res) => {
        console.log('Search results:', res.data);
      })
      .catch((err) => {
        console.error('Search error:', err);
      });
  }, [debouncedSearch]);

  const handleApply = () => {
    if (formikRef.current) {
      formikRef.current.submitForm()
        .then(() => {
          setPage(0);
          const skip = 0;
          const take = pageSize;

          if (familyCreator) {
            getStatisticsCreatorsInfo(values, setData, skip, take);
          } else {
            getStatisticsUsersInfo(values, setData, skip, take);
          }
        })
        .catch(err => {
          console.error('Form submission error:', err);
        });
    }
  };

  const getCorrectReports = (values: any, setData: any) => {
    if (familyCreator) {
      getSuperAdminReports(values, setData);
    } else {
      getUserReports(values, setData);
    }
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const timeout = setTimeout(() => {
      handleApply();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [formikValues.searchTable]);

  return (
    <UploadEntityFormDate>
      <UploadEntityFormRow container xs={12} md={12} sm={12}>
        <Grid item xs={12} md={5} sm={5}>
          <ReportsBlockWrapper>
            <Grid item xs={12} sm={4}>
              <UploadFormLabel>Registered Date</UploadFormLabel>
            </Grid>
            <Grid item xs={12} sm={5}>
              <FastField
                name="registeredRange"
                startName="registeredRange.startDate"
                endName="registeredRange.endDate"
                component={DateRangeField}
              />
            </Grid>
          </ReportsBlockWrapper>

          <ReportsBlockWrapper>
            <Grid item xs={12} sm={4}>
              <UploadFormLabel>Last Activity</UploadFormLabel>
            </Grid>
            <Grid item xs={12} sm={5}>
              <FastField
                name="activityRange"
                startName="activityRange.startDate"
                endName="activityRange.endDate"
                component={DateRangeField}
              />
            </Grid>
          </ReportsBlockWrapper>

          <ReportsBlockWrapper>
            <Grid item xs={12} sm={4}>
              <UploadFormLabel>Soft Delete Date</UploadFormLabel>
            </Grid>
            <Grid item xs={12} sm={5}>
              <FastField
                name="softDeleteRange"
                startName="softDeleteRange.startDate"
                endName="softDeleteRange.endDate"
                component={DateRangeField}
              />
            </Grid>
          </ReportsBlockWrapper>

          <ReportsBlockWrapper>
            <Grid item xs={12} sm={4}>
              <UploadFormLabel>Hard Delete Date</UploadFormLabel>
            </Grid>
            <Grid item xs={12} sm={5}>
              <FastField
                name="hardDeleteRange"
                startName="hardDeleteRange.startDate"
                endName="hardDeleteRange.endDate"
                component={DateRangeField}
              />
            </Grid>
          </ReportsBlockWrapper>

          <ReportsBlockWrapper>
            <FormControl component="fieldset">
              <Grid container spacing={2}>
                <Grid item xs={5} sm={4}>
                  <UploadFormLabel>User Type</UploadFormLabel>
                </Grid>
                <Grid item xs={7} md={5} sm={3}>
                  <RadioGroupWrapper
                    aria-label="user-type"
                    name="userType"
                    value={values.isDeleted ? 'deleted' : 'active'}
                    onChange={(event) => {
                      const isDeleted = event.target.value === 'deleted';
                      setFieldValue('isDeleted', isDeleted);
                    }}
                  >
                    <FormControlLabel value="active" control={<Radio />} label="Active Users" />
                    <FormControlLabel value="deleted" control={<Radio />} label="Deleted Users" />
                  </RadioGroupWrapper>
                </Grid>
              </Grid>
            </FormControl>
          </ReportsBlockWrapper>
        </Grid>

        <ApplyWrapper item xs={12} md={5} sm={5}>
          <ReportsBlockWrapper>
            <Grid item xs={12} sm={4}>
              <UploadFormLabel>Search</UploadFormLabel>
            </Grid>
            <RightSearchParams item xs={12} sm={8}>
              <FastField
                name="searchTable"
                placeholder="Enter text to search"
                component={TextInputField}
              />
            </RightSearchParams>
          </ReportsBlockWrapper>

          <ReportsBlockWrapper>
            <Grid item xs={12} sm={4}>
              <UploadFormLabel>Platform Type</UploadFormLabel>
            </Grid>
            <RightSearchParams item xs={12} sm={8}>
              <FastField
                name="platformType"
                options={platformType}
                component={SelectField}
              />
            </RightSearchParams>
          </ReportsBlockWrapper>

          <ReportsBlockWrapper>
            <Grid item xs={12} sm={4}>
              <UploadFormLabel>Family Role</UploadFormLabel>
            </Grid>
            <RightSearchParams item xs={12} sm={8}>
              <FastField
                name="familyRole"
                options={familyRolesCalendar}
                component={SelectField}
              />
            </RightSearchParams>
          </ReportsBlockWrapper>

          <ReportsBlockWrapper>
            <Grid item xs={12} sm={4}>
              <UploadFormLabel>Payment Type</UploadFormLabel>
            </Grid>
            <RightSearchParams item xs={12} sm={8}>
              <FastField
                name="paymentPlatformType"
                options={paymentPlatformType}
                component={SelectField}
              />
            </RightSearchParams>
          </ReportsBlockWrapper>

          <ReportsBlockWrapper>
            <Grid item sm={1} md={2} />
            <RightSearchParamsBtn container spacing={1} xs={12} sm={11} md={10}>
              <Grid item xs={12} md={4} sm={4}>
                <Button
                  fullWidth
                  type="button"
                  color="primary"
                  variant="contained"
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </Grid>
              <Grid item xs={12} md={8} sm={8}>
                <Button
                  fullWidth
                  type="button"
                  size="medium"
                  color="primary"
                  variant="contained"
                  onClick={() => getCorrectReports(values, setData)}
                >
                  Export the .xlsx table
                </Button>
              </Grid>
            </RightSearchParamsBtn>
          </ReportsBlockWrapper>
        </ApplyWrapper>
      </UploadEntityFormRow>
    </UploadEntityFormDate>
  );
};
